import React from "react";
import { useDispatch, useSelector } from "react-redux";
import useSegmentSettings from "../../../../hooks/useSegmentSetting";
import { openModal } from "../../../../redux/reducers/dialog";
import {
  pushToHistoryAction,
  removeWheelSegment,
  setWheelSegments,
} from "../../../../redux/reducers/segmentBuilder";
import {
  getSelectedSegment,
  getWheelSegments,
} from "../../../../redux/selectors/segmentBuilderSelectors";
import { ASSETS } from "../../../../utils/config";
import SegmentSettings from "../../../SegmentSettings";
import DefaultSidePane from "../../Shared/DefaultSidePane";
import SidePaneWrapper from "../../Shared/SidePaneWrapper";
import { Container, Pane } from "../../Toolbar/styles";

function SegmentSetting({ isVisible }) {
  const selectedArc = useSelector(getSelectedSegment);
  const wheelData = useSelector(getWheelSegments);
  const { handleChange, getMaxPosition } = useSegmentSettings();

  const dispatch = useDispatch();

  const onConfirm = () => {
    // Find the index of the segment being deleted
    const segmentIndex = wheelData.findIndex(
      (segment) => segment.id === selectedArc?.id
    );

    if (segmentIndex === -1) return; // Segment not found

    // Create a new array without the deleted segment
    const updatedWheelData = wheelData.filter(
      (segment) => segment.id !== selectedArc?.id
    );

    // Determine which segment to select next
    let nextSelectedIndex;

    if (updatedWheelData.length === 0) {
      // No segments left, nothing to select
      dispatch(removeWheelSegment(selectedArc?.id));
      dispatch(pushToHistoryAction());
      return;
    } else if (segmentIndex < updatedWheelData.length) {
      // Select the segment at the same position (which is now the next segment)
      nextSelectedIndex = segmentIndex;
    } else {
      // If deleting the last segment, select the new last segment
      nextSelectedIndex = updatedWheelData.length - 1;
    }

    // Update the wheel data to mark the next segment as selected
    const finalWheelData = updatedWheelData.map((segment, index) => ({
      ...segment,
      selected: index === nextSelectedIndex,
    }));

    // Dispatch action to update wheel segments
    dispatch(setWheelSegments(finalWheelData));
    dispatch(pushToHistoryAction());
  };

  const handleDelete = () => {
    dispatch(
      openModal({
        onConfirm,
        title: "Delete Segment",
        subtitle: "Are you sure you want to delete this Segment?",
      })
    );
  };

  const handleDuplicate = () => {
    const newWheelData = wheelData.map((d, i) => ({
      ...d,
      position: typeof d.position === "number" ? d.position : (i + 1) * 10,
    }));

    const id = newWheelData.length + 1;

    //calculating position of the last segment
    const lastPosition = getMaxPosition(newWheelData);

    //getting index of the selected segment
    const currIndex = newWheelData.findIndex((e) => e.selected);

    //calculating the position where the new segment should be added
    let currentPos = 10;
    if (currIndex !== -1) {
      const nextIndex = currIndex + 1;
      currentPos =
        nextIndex < newWheelData.length
          ? newWheelData[nextIndex].position
          : lastPosition + 10;
    } else if (newWheelData.length > 0) {
      currentPos = lastPosition + 10;
    }

    //changing positions of the other segments
    const updatedWheelData = newWheelData.map((d, i) => ({
      ...d,
      selected: false,
      position: currIndex > -1 && i > currIndex ? d.position + 10 : d.position,
    }));

    const adjustedIndex =
      currIndex !== -1 ? currIndex : newWheelData.length - 1;

    updatedWheelData.splice(adjustedIndex + 1, 0, {
      ...selectedArc,
      id: id,
      position: currentPos,
      selected: true,
      settings: { ...selectedArc.settings, id },
    });

    dispatch(setWheelSegments(updatedWheelData));
    dispatch(pushToHistoryAction()); // Push the new state to history
  };

  return (
    <>
      <Container>
        <Pane isVisible={selectedArc}>
          <SidePaneWrapper
            isVisible={isVisible}
            title="Segment Settings"
            icon={ASSETS.wheelSetting}
            handleDelete={handleDelete}
            handleDuplicate={handleDuplicate}
          >
            <div style={{ marginTop: 20 }}>
              <SegmentSettings
                segmentSettings={selectedArc?.settings || {}}
                handleChange={handleChange}
                isHideHeader={true}
              />
            </div>
          </SidePaneWrapper>
        </Pane>
        <DefaultSidePane
          title={"Segment Settings"}
          titleImage={ASSETS.segmentEditor.SegmentIconV2}
          text={
            "Select a segment on your wheel or create new - for the settings to show!"
          }
          selectedArc={selectedArc}
        />
      </Container>
    </>
  );
}

export default SegmentSetting;
